import { PlanName } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { getLogoutHref, getProjectUrl } from 'base/blocks_routes';
import { delay, UnreachableError } from 'base/helpers';
import { Avatar } from 'base/ui/avatar';
import { Button, ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import { IconClear, IconSave, IconSearch, IconStars } from 'base/ui/icons';
import { FormInput } from 'base/ui/input';
import { useAnchoredMenu, useOnMousedownOutside } from 'base/ui/menu';
import { menuDivider, menuItem, MenuModel } from 'base/ui/menu_model';
import { PlacementFunction, usePinElementPlacement } from 'base/ui/tooltip';
import { useDebouncedValue } from 'base/useDebounce';
import { useToggle } from 'base/useToggle';
import { ProFeatureGateDialog } from 'blocks/billing/feature_gate_dialog';
import { BlocksService } from 'blocks/blocks_service';
import { CreateProjectErrorDialog } from 'blocks/create_project';
import { currentUrl } from 'blocks/current_url';
import { EMPTY_PROJECT } from 'blocks/editor/default_project';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import { manageAccountRouteToUrl } from 'blocks/manage_account/routes';
import { SitemapImportDialog } from 'blocks/sitemap_import/sitemap_import_dialog';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './nav.css';
import { ProjectsDataStore } from './project_data/projects_data_store';
import { maybeParseDashboardRoute } from './routes';
import { SearchMode, SEARCH_MODE_OPTIONS } from './search';
export function NavSearch({
  renderResults,
}: {
  renderResults: (query: string, mode: SearchMode) => React.ReactNode;
}) {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const [mode, setMode] = React.useState<SearchMode>(() => getInitialMode());
  const [isOpen, setOpen] = React.useState(false);

  usePinElementPlacement(isOpen, anchorRef, popupRef, placePopupUnderSearch);
  useOnMousedownOutside(
    isOpen,
    React.useCallback(() => setOpen(false), []),
    popupRef,
    anchorRef,
  );

  useKeyboardShortcuts(true, {
    [shortcut('k', { cmd: true })]: () => {
      inputRef.current?.focus();
    },
  });
  const handleFocus = React.useCallback(() => setOpen(true), []);
  const handleKeyUp = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }, []);

  return (
    <div className={styles.search}>
      <div className={styles.searchInner} ref={anchorRef}>
        <FormInput
          ref={inputRef}
          iconStart={IconSearch}
          type="search"
          placeholder="Search for a project or a component"
          value={query}
          onChange={setQuery}
          onFocus={handleFocus}
          onKeyUp={handleKeyUp}
          buttonEnd={
            query ? (
              <Button
                size="small"
                variant="subtle"
                icon={IconClear}
                onClick={() => setQuery('')}
              />
            ) : undefined
          }
          variant="darker"
          size="large"
          textSize="medium"
        />
      </div>
      {isOpen &&
        createPortal(
          <div className={styles.searchPopup} ref={popupRef}>
            <ButtonPalette>
              <RadioButtonGroup
                options={SEARCH_MODE_OPTIONS}
                value={mode}
                onChange={setMode}
              />
            </ButtonPalette>
            {renderResults(debouncedQuery, mode)}
          </div>,
          document.body,
        )}
    </div>
  );
}

function getInitialMode(): SearchMode {
  const route = maybeParseDashboardRoute(currentUrl);
  if (!route) {
    if (currentUrl.pathname.startsWith('/react')) {
      return 'react';
    } else {
      return 'webflow'; // probably a webflow library page
    }
  }
  switch (route.path) {
    case 'dashboard':
      return 'projects'; // main dashboard page shows a project list
    default:
      throw new UnreachableError(route.path);
  }
}

const placePopupUnderSearch: PlacementFunction = (search, popup, popupEl) => {
  const pad = 12;
  const top = search.bottom + pad;
  const width = Math.max(
    search.width,
    Math.min(
      window.innerWidth - 2 * pad,
      47.5 * 16, // 760px
    ),
  );
  const left = Math.max(
    Math.min(
      // align with search box left
      search.left,
      // don't go off the right edge of the screen
      window.innerWidth - width - pad,
    ),
    // don't go off the left edge of the screen
    pad,
  );

  popupEl.style.top = `${top}px`;
  popupEl.style.left = `${left}px`;
  popupEl.style.width = `${width}px`;
};

export const NavImportSitemap = observer(function _NavImportSitemap({
  owner,
  accountStore,
  blocksService,
  projectsListStore,
  analytics,
}: {
  owner: 'user' | 'team';
  accountStore: AccountStore;
  blocksService: BlocksService;
  projectsListStore: ProjectsDataStore;
  analytics: AnalyticsService;
}) {
  const [showImportSitemap, setShowImportSitemap] = React.useState(false);
  const {
    toggleOn: openUpgradeDialog,
    toggleOff: closeUpgradeDialog,
    value: showUpgradeDialog,
  } = useToggle();

  const handleClick = () => {
    if (projectsListStore.state.type !== 'loaded') {
      return;
    }

    analytics.track({
      event: 'dashboard:import_button_click',
    });

    const projectsQuota = projectsListStore.state.res?.projectsQuota;
    if (
      projectsQuota?.limit != null &&
      projectsQuota.used >= projectsQuota.limit
    ) {
      return openUpgradeDialog();
    }
    setShowImportSitemap(true);
  };

  return (
    <>
      <Button
        icon={IconSave}
        label="Import from URL"
        onClick={handleClick}
        disabled={projectsListStore.state.type === 'loading'}
      />
      {showImportSitemap ? (
        <SitemapImportDialog
          blocksService={blocksService}
          onClose={() => setShowImportSitemap(false)}
          owner={owner}
          analytics={analytics}
          importBehaviour={{ kind: 'import-into-new-project' }}
        />
      ) : null}
      {showUpgradeDialog && (
        <ProFeatureGateDialog
          analytics={analytics}
          currentPlan={accountStore.member?.planName ?? PlanName.FREE}
          initialFeatureKey="projects"
          onClose={closeUpgradeDialog}
        />
      )}
    </>
  );
});

export const NavNewProject = observer(function _NavNewProject({
  owner,
  accountStore,
  projectsListStore,
  blocksService,
  analytics,
}: {
  owner: 'user' | 'team';
  accountStore: AccountStore;
  projectsListStore: ProjectsDataStore;
  blocksService: BlocksService;
  analytics: AnalyticsService;
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  const create = useCallback(
    async () => {
      if (loading) return;
      setLoading(true);
      try {
        const createRes = await blocksService.createProject({
          data: EMPTY_PROJECT,
          owner,
        });
        analytics.track({
          event: 'dashboard:project_create',
          owner,
        });
        window.location.href = getProjectUrl(createRes.token);
        await delay(3_000); // allow time for the browser to load the new page
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owner, loading],
  );

  const {
    toggleOn: openUpgradeDialog,
    toggleOff: closeUpgradeDialog,
    value: showUpgradeDialog,
  } = useToggle();

  const handleClick = () => {
    if (projectsListStore.atProjectLimit) {
      return openUpgradeDialog();
    }

    void create();
  };

  return (
    <>
      <Button
        icon={IconStars}
        label={owner === 'team' ? 'New Team Project' : 'New Project'}
        variant="primary"
        background="ai"
        onClick={handleClick}
        loading={loading}
        disabled={projectsListStore.state.type === 'loading'}
      />
      <CreateProjectErrorDialog
        error={error}
        onClose={() => window.location.reload()}
        me={accountStore.member}
        analytics={analytics}
      />
      {showUpgradeDialog && (
        <ProFeatureGateDialog
          currentPlan={accountStore.member?.planName ?? PlanName.FREE}
          initialFeatureKey="projects"
          onClose={closeUpgradeDialog}
          analytics={analytics}
        />
      )}
    </>
  );
});

export const NavUser = observer(function _NavUser({
  accountStore,
}: {
  accountStore: AccountStore;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const me = accountStore.member;

  const onClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);
  const onClickOrContextMenu = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsOpen((v) => !v);
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  );
  const [portal] = useAnchoredMenu(isOpen, onClose, buttonRef, USER_MENU, {
    search: 'hidden',
  });

  return (
    <>
      <button
        className={styles.userButton}
        ref={buttonRef}
        onClick={onClickOrContextMenu}
        onContextMenu={onClickOrContextMenu}
      >
        <Avatar size="large" {...me} />
      </button>
      {portal}
    </>
  );
});

const USER_MENU: MenuModel = [
  menuItem({
    label: 'Manage Account',
    href: manageAccountRouteToUrl({ path: 'manage-account' }),
  }),
  menuItem({
    label: 'Saved',
    href: '/saved-components', // webflow page
  }),
  menuItem({
    label: 'Your Library',
    href: '/your-library', // webflow page
  }),
  menuDivider(),
  /* TODO we need to make these pages in webflow first
    menuItem({
      label: 'Getting started',
      href: '/getting-started', // webflow page
    }),
    menuItem({
      label: 'Help',
      href: '/help', // webflow page
    }),
     */
  menuItem({
    label: 'Log out',
    href: getLogoutHref(),
  }),
];
